/**
 * Referrals tab — share-your-link + earnings dashboard.
 *
 * - Pulls /api/referrals/me on mount.
 * - Copy-to-clipboard for both the bare 8-char code and the full link.
 * - Renders the four stat tiles + the £/month cap progress bar so the
 *   user can see exactly how much credit they've earned and how much
 *   headroom is left this month.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Gift, Users, Hourglass, PoundSterling } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { fmtMoney } from '../../lib/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pounds = (pence) => `£${fmtMoney((pence || 0) / 100)}`;

const StatTile = ({ icon: Icon, label, value, testId, hint }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-wider font-semibold">
      <Icon size={14} className="text-[#0052FF]" />
      {label}
    </div>
    <div className="mt-2 text-2xl font-extrabold text-[#020617]">{value}</div>
    {hint && <div className="text-xs text-[#94A3B8] mt-1">{hint}</div>}
  </div>
);

const ReferralsTab = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null); // 'code' | 'link' | null

  useEffect(() => {
    axios.get(`${API}/referrals/me`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load your referral details.'));
  }, []);

  const shareLink = data
    ? `${window.location.origin}/?ref=${data.referral_code}`
    : '';

  const copy = async (which, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      toast.success(which === 'code' ? 'Code copied.' : 'Link copied.');
      setTimeout(() => setCopied(null), 1800);
    } catch (_e) {
      toast.error('Could not access clipboard — copy manually.');
    }
  };

  if (!data) return <div className="text-sm text-[#64748B]">Loading…</div>;

  const { stats, monthly_cap_pence, credit_pence_per_side } = data;
  const capUsedPct = Math.min(
    100,
    Math.round((stats.this_month_earned_pence / monthly_cap_pence) * 100),
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="referrals-tab">
      {/* Header card with the share link & code. */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="referrals-share-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-[#EFF6FF] flex items-center justify-center text-[#0052FF]">
            <Gift size={20} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#020617]">Refer a friend, get £{fmtMoney(credit_pence_per_side / 100)} off your subscription</h2>
            <p className="text-sm text-[#64748B] mt-1">
              Share the link below. When your friend subscribes to a paid Forge Ledger
              tier and pays their first invoice, we&apos;ll add £{fmtMoney(credit_pence_per_side / 100)} of
              credit to <strong>your Forge Ledger billing</strong> (auto-applied to your next
              monthly or annual invoice) — and your friend gets £{fmtMoney(credit_pence_per_side / 100)} off
              theirs too. Up to £{fmtMoney(monthly_cap_pence / 100)} per month.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
              Your referral link
            </label>
            <div className="mt-1.5 flex gap-2">
              <input
                readOnly
                value={shareLink}
                className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#020617] font-mono"
                data-testid="referral-link-input"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                onClick={() => copy('link', shareLink)}
                className="bg-[#0052FF] text-white hover:bg-[#003ECC] rounded-lg px-4 inline-flex items-center gap-1.5"
                data-testid="referral-copy-link-button"
              >
                {copied === 'link' ? <Check size={16} /> : <Copy size={16} />}
                {copied === 'link' ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
              Or share just the code
            </label>
            <div className="mt-1.5 flex gap-2">
              <input
                readOnly
                value={data.referral_code}
                className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#020617] font-mono tracking-widest"
                data-testid="referral-code-input"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => copy('code', data.referral_code)}
                className="border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC] rounded-lg px-4 inline-flex items-center gap-1.5"
                data-testid="referral-copy-code-button"
              >
                {copied === 'code' ? <Check size={16} /> : <Copy size={16} />}
                {copied === 'code' ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="referrals-stats-grid">
        <StatTile
          icon={Users}
          label="Friends signed up"
          value={stats.pending + stats.credited + stats.blocked}
          testId="referrals-stat-total"
          hint={`${stats.credited} paid · ${stats.pending} pending`}
        />
        <StatTile
          icon={PoundSterling}
          label="Subscription credit earned"
          value={pounds(stats.total_earned_pence)}
          testId="referrals-stat-total-earned"
          hint="Auto-applied to your next invoice"
        />
        <StatTile
          icon={Hourglass}
          label="Pending"
          value={stats.pending}
          testId="referrals-stat-pending"
          hint="Signed up, not yet paid"
        />
        <StatTile
          icon={Gift}
          label="This month earned"
          value={pounds(stats.this_month_earned_pence)}
          testId="referrals-stat-month"
          hint={`${pounds(stats.this_month_remaining_pence)} left of monthly cap`}
        />
      </div>

      {/* Monthly cap progress */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="referrals-cap-card">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-[#020617]">Monthly credit cap</div>
          <div className="text-sm text-[#64748B]">
            <span className="font-bold text-[#020617]">{pounds(stats.this_month_earned_pence)}</span>
            {' '}/ {pounds(monthly_cap_pence)}
          </div>
        </div>
        <div className="h-2 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#0052FF] transition-all"
            style={{ width: `${capUsedPct}%` }}
            data-testid="referrals-cap-progress"
          />
        </div>
        <div className="text-xs text-[#64748B] mt-2">
          Resets the 1st of every month. Friends you refer above the cap won&apos;t earn
          you a credit, but they&apos;ll still get theirs.
        </div>
      </div>

      {/* Fine print */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5 text-xs text-[#64748B] leading-relaxed" data-testid="referrals-fine-print">
        <strong className="text-[#020617]">How it works:</strong> credit is added to
        your Forge Ledger billing balance (not your bank account) and auto-applied to
        your next subscription invoice. It triggers when your friend pays their first
        Forge Ledger subscription invoice. Credits don&apos;t stack with founding-member
        promotions. Self-referrals don&apos;t count. We may withhold credits in cases of
        suspected fraud. See our <a href="/help" className="text-[#0052FF] hover:underline">Help page</a> for full details.
      </div>
    </div>
  );
};

export default ReferralsTab;
